/**
 * SCIM Module Constants
 * Centralized configuration values for the SCIM module
 */

export const SCIM_CONSTANTS = {
  /**
   * Polling configuration for async sync status checks
   */
  POLLING: {
    /** How often to poll for sync status (milliseconds) */
    INTERVAL_MS: 5000, // 5 seconds

    /** Maximum number of polling attempts before timeout */
    MAX_POLLS: 60, // 5 minutes (60 × 5s)

    /** Initial delay before starting to poll (milliseconds) */
    INITIAL_DELAY_MS: 3000, // 3 seconds
  },

  /**
   * Batch processing configuration
   */
  BATCH: {
    /** Number of items to process before flushing batch */
    SIZE: 100,

    /** Maximum number of detailed log items to store */
    DETAILED_LOG_MAX_ITEMS: 1000,

    /** Maximum data size per detailed log item (bytes) */
    MAX_DATA_SIZE_BYTES: 1000, // 1KB
  },

  /**
   * Pagination configuration
   */
  PAGINATION: {
    /** Default number of items per page */
    DEFAULT_PAGE_SIZE: 10,

    /** Available page size options */
    PAGE_SIZE_OPTIONS: [10, 20, 50],

    /** Maximum items per page */
    MAX_PAGE_SIZE: 100,
  },

  /**
   * Sync configuration defaults
   */
  SYNC: {
    /** Default batch size for SCIM operations */
    DEFAULT_BATCH_SIZE: 100,

    /** Timeout for SCIM API calls (milliseconds) */
    API_TIMEOUT_MS: 30000, // 30 seconds

    /** Number of retry attempts for failed operations */
    MAX_RETRY_ATTEMPTS: 3,
  },

  /**
   * Data retention configuration
   */
  RETENTION: {
    /** Default retention period for sync logs (days) */
    SYNC_LOG_RETENTION_DAYS: 90,

    /** Warn when logs older than this (days) */
    WARNING_THRESHOLD_DAYS: 60,
  },

  /**
   * UI refresh intervals
   */
  REFRESH: {
    /** How often to auto-refresh statistics (milliseconds) */
    STATS_INTERVAL_MS: 60000, // 1 minute

    /** Debounce delay for search input (milliseconds) */
    SEARCH_DEBOUNCE_MS: 500,

    /** Delay before auto-refreshing after sync completion (milliseconds) */
    POST_SYNC_DELAY_MS: 2000, // 2 seconds
  },

  /**
   * Provider types
   */
  PROVIDERS: {
    SKILLMINE_AUTH: 'SKILLMINE_AUTH',
    SCIM: 'SCIM',
  },

  /**
   * Conflict resolution strategies
   */
  CONFLICT_RESOLUTION: {
    SKIP: 'SKIP',
    UPDATE: 'UPDATE',
    MERGE: 'MERGE',
  },

  /**
   * Sync statuses
   */
  SYNC_STATUS: {
    INITIATED: 'INITIATED',
    RUNNING: 'RUNNING',
    COMPLETED: 'COMPLETED',
    SUCCESS: 'SUCCESS',
    FAILED: 'FAILED',
    ERROR: 'ERROR',
    PARTIAL: 'PARTIAL',
  },
} as const;

/**
 * Provider display configuration
 */
export const PROVIDER_CONFIG = {
  SKILLMINE_AUTH: {
    label: 'Skillmine Auth (CustomGroups)',
    icon: '🔵',
    color: 'bg-indigo-500',
    description: 'Skillmine authentication with custom groups and roles support',
  },
  SCIM: {
    label: 'SCIM 2.0 (Standard)',
    icon: '🔗',
    color: 'bg-blue-500',
    description: 'Standard SCIM 2.0 protocol (compatible with Azure AD, Okta, etc.)',
  },
} as const;

/**
 * Helper functions for provider configuration
 */
export const getProviderIcon = (providerType: string): string => {
  const type = providerType.toUpperCase();
  return PROVIDER_CONFIG[type as keyof typeof PROVIDER_CONFIG]?.icon || '🔒';
};

export const getProviderColor = (providerType: string): string => {
  const type = providerType.toUpperCase();
  return PROVIDER_CONFIG[type as keyof typeof PROVIDER_CONFIG]?.color || 'bg-gray-500';
};

export const getProviderLabel = (providerType: string): string => {
  const type = providerType.toUpperCase();
  return PROVIDER_CONFIG[type as keyof typeof PROVIDER_CONFIG]?.label || providerType;
};
